import { onScopeDispose, toValue, watch, type MaybeRefOrGetter } from "vue";

/**
 * The last step of the breadcrumb trail, for a page that is about one entity.
 *
 * `definePageMeta({ crumb })` is fixed at build time, so an entity page could
 * only put its type there: the trail ended `… / Dataset`, `… / Model`,
 * `… / Conversation` while the entity's own name sat in the heading right below
 * it (W8-UI-03). A page that knows its entity's name publishes it here and the
 * shell shows that instead; the type word stays as what is shown until the read
 * answers, and for every page that is not about one entity.
 */
export function useEntityCrumbName() {
  return useState<string>("entity-crumb", () => "");
}

export function useEntityCrumb(name: MaybeRefOrGetter<string | null | undefined>) {
  const crumb = useEntityCrumbName();
  let published = "";
  watch(
    () => toValue(name) ?? "",
    (value) => {
      published = value;
      crumb.value = value;
    },
    { immediate: true },
  );
  // Only the page that put the name there takes it away: leaving a name behind
  // would label the next page with the previous entity, and clearing one the
  // next page has already published would blank a trail that is correct.
  onScopeDispose(() => {
    if (crumb.value === published) crumb.value = "";
  });
}
